import { useState } from "react";
import type { ChangeEvent, FormEvent } from "react";
import {
  Mls2ApiError,
  uploadBrokerInventory,
  type BrokerInventoryItem,
  type BrokerInventoryPayload,
  type BrokerUploadResult,
} from "../lib/api";
import { requireAuth } from "../lib/auth";
const MAX_FILE_BYTES = 1_000_000;
const PREVIEW_LIMIT = 10;

interface ParsedUpload {
  fileName: string;
  format: "CSV" | "JSON";
  rows: BrokerInventoryItem[];
  payload: BrokerInventoryPayload;
}

export function BrokerUpload() {
  const [parsed, setParsed] = useState<ParsedUpload | null>(null);
  const [results, setResults] = useState<BrokerUploadResult[] | null>(null);
  const [isParsing, setIsParsing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleFileChange(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    setParsed(null);
    setResults(null);
    setError(null);

    if (!file) return;
    if (file.size > MAX_FILE_BYTES) {
      setError("File exceeds the 1 MB upload limit.");
      event.target.value = "";
      return;
    }

    setIsParsing(true);
    try {
      const text = await file.text();
      const nextParsed = parseInventoryFile(file.name, file.type, text);
      if (!nextParsed.rows.length) {
        throw new Error("The file does not contain any inventory rows.");
      }
      if (nextParsed.rows.length > 500) {
        throw new Error("A single upload may contain at most 500 listings.");
      }
      setParsed(nextParsed);
    } catch (parseError) {
      setError(
        parseError instanceof Error ? parseError.message : "Unable to parse file.",
      );
      event.target.value = "";
    } finally {
      setIsParsing(false);
    }
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!parsed) return;

    const jwt = requireAuth();
    if (!jwt) {
      setError("Sign in before uploading broker inventory.");
      return;
    }

    setIsUploading(true);
    setError(null);
    setResults(null);

    try {
      const response = await uploadBrokerInventory(jwt, parsed.payload);
      setResults(response.results);
    } catch (uploadError) {
      setError(
        uploadError instanceof Mls2ApiError
          ? uploadError.message
          : "Inventory upload failed.",
      );
    } finally {
      setIsUploading(false);
    }
  }

  const successful = results?.filter((result) => result.ok) ?? [];
  const failed = results?.filter((result) => !result.ok) ?? [];

  return (
    <section className="mx-auto max-w-7xl">
      <header>
        <p className="mb-2 text-xs font-semibold uppercase tracking-[0.18em] text-mls-600">
          Broker inventory
        </p>
        <h1 className="text-3xl font-semibold tracking-tight text-slate-950">
          Upload inventory
        </h1>
        <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600">
          Upload JSON or CSV containing normalized property facts. Files are
          previewed locally before submission and may contain up to 500 rows.
        </p>
      </header>

      <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <label className="block">
            <span className="text-sm font-semibold text-slate-900">
              Inventory file
            </span>
            <span className="mt-1 block text-xs text-slate-500">
              Accepted formats: .csv, .json · Maximum size: 1 MB
            </span>
            <input
              className="mt-4 block w-full cursor-pointer rounded-xl border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-600 file:mr-4 file:rounded-lg file:border-0 file:bg-mls-900 file:px-4 file:py-2 file:font-semibold file:text-white hover:border-mls-600"
              type="file"
              accept=".csv,.json,text/csv,application/json"
              onChange={handleFileChange}
              disabled={isParsing || isUploading}
            />
          </label>
        </div>

        {error ? (
          <p
            className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
            role="alert"
          >
            {error}
          </p>
        ) : null}

        {isParsing ? <ParsingState /> : null}

        {parsed ? (
          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="flex flex-col gap-2 border-b border-slate-200 px-6 py-5 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h2 className="font-semibold text-slate-950">Data preview</h2>
                <p className="mt-1 text-xs text-slate-500">{parsed.fileName}</p>
              </div>
              <div className="flex gap-2 text-xs font-semibold">
                <span className="rounded-full bg-mls-50 px-3 py-1.5 text-mls-600">
                  {parsed.format}
                </span>
                <span className="rounded-full bg-slate-100 px-3 py-1.5 text-slate-600">
                  {parsed.rows.length} rows
                </span>
              </div>
            </div>
            <PreviewTable rows={parsed.rows.slice(0, PREVIEW_LIMIT)} />
            {parsed.rows.length > PREVIEW_LIMIT ? (
              <p className="border-t border-slate-100 px-6 py-3 text-xs text-slate-500">
                Showing the first {PREVIEW_LIMIT} of {parsed.rows.length} rows.
              </p>
            ) : null}
          </div>
        ) : null}

        {parsed ? (
          <div className="flex justify-end">
            <button
              className="rounded-lg bg-mls-900 px-5 py-3 text-sm font-semibold text-white transition hover:bg-mls-600 disabled:cursor-not-allowed disabled:opacity-60"
              type="submit"
              disabled={isUploading}
            >
              {isUploading
                ? "Uploading " + parsed.rows.length + " rows…"
                : "Upload " + parsed.rows.length + " listings"}
            </button>
          </div>
        ) : null}
      </form>

      {results ? (
        <section className="mt-8 grid gap-6 lg:grid-cols-2" aria-live="polite">
          <ResultCard
            title={"Canonical listings created or matched (" + successful.length + ")"}
            tone="success"
          >
            {successful.length ? (
              <ul className="space-y-2">
                {successful.map((result, index) =>
                  result.ok ? (
                    <li
                      className="break-all rounded-lg bg-emerald-50 px-3 py-2 font-mono text-xs text-emerald-800"
                      key={result.canonicalListingId + "-" + index}
                    >
                      {result.canonicalListingId}
                    </li>
                  ) : null,
                )}
              </ul>
            ) : (
              <p className="text-sm text-slate-500">No listings were accepted.</p>
            )}
          </ResultCard>

          <ResultCard title={"Rows requiring attention (" + failed.length + ")"} tone="error">
            {failed.length ? (
              <ol className="space-y-3">
                {failed.map((result, index) =>
                  !result.ok ? (
                    <li className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" key={index}>
                      <span className="font-semibold">Row {index + 1}: </span>
                      {result.errors.join(", ")}
                    </li>
                  ) : null,
                )}
              </ol>
            ) : (
              <p className="text-sm text-slate-500">All rows were accepted.</p>
            )}
          </ResultCard>
        </section>
      ) : null}
    </section>
  );
}

function PreviewTable({ rows }: { rows: BrokerInventoryItem[] }) {
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-slate-200 text-left text-sm">
        <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
          <tr>
            {["City", "Neighborhood", "Price", "Rooms", "Area", "Floor"].map(
              (heading) => (
                <th className="px-5 py-3 font-semibold" scope="col" key={heading}>
                  {heading}
                </th>
              ),
            )}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.map((row, index) => (
            <tr key={index}>
              <Cell value={row.city} />
              <Cell value={row.neighborhood} />
              <Cell value={row.price} />
              <Cell value={row.rooms} />
              <Cell value={row.sqm} />
              <Cell value={row.floor} />
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Cell({ value }: { value: unknown }) {
  return (
    <td className="max-w-56 truncate whitespace-nowrap px-5 py-3 text-slate-700">
      {value === null || value === undefined || value === "" ? "—" : String(value)}
    </td>
  );
}

function ResultCard({
  title,
  tone,
  children,
}: {
  title: string;
  tone: "success" | "error";
  children: React.ReactNode;
}) {
  return (
    <article
      className={
        "rounded-2xl border bg-white p-6 shadow-sm " +
        (tone === "success" ? "border-emerald-200" : "border-red-200")
      }
    >
      <h2 className="mb-4 font-semibold text-slate-950">{title}</h2>
      {children}
    </article>
  );
}

function ParsingState() {
  return (
    <div className="animate-pulse rounded-2xl border border-slate-200 bg-white p-6">
      <div className="h-4 w-32 rounded bg-slate-200" />
      <div className="mt-5 h-24 rounded bg-slate-100" />
    </div>
  );
}

function parseInventoryFile(
  fileName: string,
  mimeType: string,
  text: string,
): ParsedUpload {
  const isJson =
    fileName.toLowerCase().endsWith(".json") ||
    mimeType.includes("application/json");

  if (isJson) {
    const value: unknown = JSON.parse(text);
    const values = Array.isArray(value) ? value : [value];
    if (!values.every(isInventoryRow)) {
      throw new Error("JSON must contain an object or an array of objects.");
    }
    return {
      fileName,
      format: "JSON",
      rows: values,
      payload: values,
    };
  }

  const records = parseCsv(text);
  return {
    fileName,
    format: "CSV",
    rows: records,
    payload: text,
  };
}

function parseCsv(text: string): BrokerInventoryItem[] {
  const rows = splitCsvRows(text).filter((row) =>
    row.some((value) => value.trim() !== ""),
  );
  const headers = rows.shift()?.map((header) => header.trim());

  if (!headers?.length) {
    throw new Error("CSV must include a header row.");
  }

  return rows.map((row) => {
    const record: Record<string, string> = {};
    headers.forEach((header, index) => {
      record[header] = row[index]?.trim() ?? "";
    });
    return record;
  });
}

function splitCsvRows(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let value = "";
  let quoted = false;

  for (let index = 0; index < text.length; index += 1) {
    const character = text[index];
    const next = text[index + 1];

    if (character === '"' && quoted && next === '"') {
      value += '"';
      index += 1;
    } else if (character === '"') {
      quoted = !quoted;
    } else if (character === "," && !quoted) {
      row.push(value);
      value = "";
    } else if ((character === "\n" || character === "\r") && !quoted) {
      if (character === "\r" && next === "\n") index += 1;
      row.push(value);
      rows.push(row);
      row = [];
      value = "";
    } else {
      value += character;
    }
  }

  if (value || row.length) {
    row.push(value);
    rows.push(row);
  }

  if (quoted) {
    throw new Error("CSV contains an unterminated quoted field.");
  }

  return rows;
}

function isInventoryRow(value: unknown): value is BrokerInventoryItem {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}